// ---------------------------------------------------------------------------
// Module-scope date formatters — constructed once per page load.
// These depend only on the browser's local timezone, which is stable for the
// lifetime of the page, so there is no need to rebuild them on every render.
// ---------------------------------------------------------------------------
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const LOCAL_BASE_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: LOCAL_TZ,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const localFormatter24h = new Intl.DateTimeFormat("en-GB", { ...LOCAL_BASE_OPTIONS, hour12: false });
const localFormatter12h = new Intl.DateTimeFormat("en-GB", { ...LOCAL_BASE_OPTIONS, hour12: true, timeZoneName: "short" });

/**
 * Formats a date string using the browser's local timezone.
 * Returns `"–"` (for 24-hour displays) or `null` (for tooltip content) when the
 * value is absent or unparseable.
 *
 * @param value   - ISO / parseable date string
 * @param hour12  - true → include AM/PM + timezone abbreviation; false → 24-hour clock
 */
export function formatLocalDate(value: string | undefined | null, hour12: boolean): string | null {
  if (!value) return hour12 ? null : "–";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return hour12 ? null : "–";
    const formatter = hour12 ? localFormatter12h : localFormatter24h;
    const parts = formatter.formatToParts(d);
    // Build a lookup map once instead of scanning the parts array per field.
    const pm: Record<string, string> = {};
    for (const p of parts) pm[p.type] = p.value;
    const base = `${pm.day ?? ""} ${pm.month ?? ""} ${pm.year ?? ""} ${pm.hour ?? ""}:${pm.minute ?? ""}`;
    if (hour12) {
      const rawTz = pm.timeZoneName ?? "";
      const tzName = rawTz.startsWith("GMT") ? rawTz.replace("GMT", "UTC") : rawTz;
      return `${base} ${(pm.dayPeriod ?? "").toUpperCase()}${tzName ? ` (${tzName})` : ""}`;
    }
    return base;
  } catch {
    return hour12 ? null : "–";
  }
}
